import { EventEmitter, Injectable } from '@angular/core';

import { UPDATES_TYPES } from '../../constants/updates-types.const';

import { SyncService } from './sync.service';
import { DbDaoService, GetAllDataOptions } from '../db/db-dao.service';
import { SecurityService } from './security.service';
import { InvoiceListService } from './invoice-list.service';
import { InvoicesService } from './invoices.service';
import { CartService } from './cart.service';

import { Invoice } from '@pos-common/classes/invoice.class';
import { Subject, BehaviorSubject } from 'rxjs';
import { TableEnforceService } from './table-select-enforcement.service';
import { LogService } from './logger/log.service';
import { NavController, mdTransitionAnimation } from '@ionic/angular';
import { ROUTE_URLS } from '@pos-common/constants/route-urls.const';
import {
  query,
  group,
  or,
  isNull,
  Query,
  by,
  oneOf,
  from,
  property,
  leftJoin,
  contains,
  QueryArray,
  Order,
} from '@paymash/capacitor-database-plugin';
import { map } from 'rxjs/operators';
import { InvoicesProvider } from '../resources/invoices-db-entity.provider';
import { InvoceInlist } from '@pos-common/classes/invoice-in-list.class';
import { StoresProvider } from '../resources/stores-db-entity.provider';
import { SALES_CHANNEL_TYPES } from '@pos-common/constants/sales-channel-types';
import { DbResponse } from '../db/db-dao.utils';
import { INVOICE_TYPES } from '@pos-common/constants/invoice-types';
import { Store } from '@pos-common/classes/store.class';
import { SalesSubChannelTypesEnum } from '@pos-common/constants';

@Injectable()
export class InvoicesFilterService {
  private readonly logger = this.logService.createLogger('InvoicesFilterService');
  constructor(
    public SyncService: SyncService,
    public DbDaoService: DbDaoService,
    private invoicesProvider: InvoicesProvider,
    public InvoiceListService: InvoiceListService,
    public InvoicesService: InvoicesService,
    public CartService: CartService,
    public TableEnforceService: TableEnforceService,
    public SecurityService: SecurityService,
    public navController: NavController,
    private storesProvider: StoresProvider,
    private logService: LogService
  ) {}

  public ivoiceFilterData: Subject<any> = new BehaviorSubject<any>(null);
  public invoiceListSubject: Subject<any> = new BehaviorSubject<any>(null);
  public deleteEvent: Subject<any> = new BehaviorSubject<any>(null);
  private updateEvent$ = new Subject<void>();
  private updateInvoiceList$ = new Subject<void>();
  public openInvoiceCount: Subject<any> = new BehaviorSubject<any>(null);
  public newTableInvoiceEvent = new EventEmitter();
  public newInvoiceEvent = new EventEmitter();
  public currentFilterSet: any = null;
  public hallList: any = [];

  public getInvoiceFilter() {
    return this.ivoiceFilterData.asObservable();
  }

  public setInvoiceFilter(newData) {
    this.currentFilterSet = newData;
    this.ivoiceFilterData.next(newData);
  }

  public emitDeleteInvoiceEvent(uuid) {
    this.deleteEvent.next(uuid);
  }

  public deleteInvoiceEvent() {
    return this.deleteEvent.asObservable();
  }

  public emitUpdateEvent() {
    this.updateEvent$.next();
  }

  public getUpdateEvent() {
    return this.updateEvent$.asObservable();
  }

  public updateInvoiceList() {
    this.updateInvoiceList$.next();
  }

  public getUpdateInvoiceList() {
    return this.updateInvoiceList$.asObservable();
  }

  public getInvoices() {
    return this.invoiceListSubject.asObservable();
  }

  public setInvoices(data: InvoceInlist[]) {
    this.invoiceListSubject.next(data);
  }

  public getInvoiceCountFromDB() {
    const { isDraft, isNotDeleted, storeUuid, isPosSalesChannel } = this.queryHelpers();

    if (!storeUuid) {
      return;
    }

    const queryParams = query(isDraft, storeUuid, isPosSalesChannel, isNotDeleted);
    return this.invoicesProvider
      .getCountByParams(queryParams)
      .toPromise()
      .then((count) => count);
  }

  public checkTableBusyStatus(tableUuid: string): Promise<boolean> {
    const { isDraft, isNotDeleted, storeUuid, isPosSalesChannel } = this.queryHelpers();

    if (!storeUuid) {
      return;
    }

    const queryParams = query({ 'gastronomyTable.uuid': tableUuid }, isDraft, storeUuid, isPosSalesChannel, isNotDeleted);
    return this.invoicesProvider
      .getCountByParams(queryParams)
      .pipe(map((value) => !!value))
      .toPromise();
  }

  private buildFilter(): Query {
    const queryParams: Query = query({
      'i.ext_type': isNull(),
    });

    let { isDraft, isNotDeleted, storeUuid, isPosSalesChannel } = this.queryHelpers('i.');

    if (!this.currentFilterSet || !storeUuid) {
      return undefined;
    }

    if (this.currentFilterSet.tab === 'open' || this.currentFilterSet.tab === 'paid') {
      isDraft = { 'i.isDraft': this.currentFilterSet.tab === 'open' };
    } else if (this.currentFilterSet.tab === 'no') {
      queryParams.push({ 'i.gastronomyTable': isNull() });
    } else {
      if (this.currentFilterSet.table === 'all') {
        queryParams.push({ 'i.gastronomyTable.uuid': oneOf(...this.currentFilterSet.tableList) });
      } else {
        queryParams.push({ 'i.gastronomyTable.uuid': this.currentFilterSet.table });
      }
    }

    queryParams.push(isDraft, storeUuid, isNotDeleted, isPosSalesChannel);

    return queryParams;
  }

  private async loadGastronomyHallItems(): Promise<void> {
    this.hallList = await this.DbDaoService.getAllDataRaw(UPDATES_TYPES.GastronomyHall.type);
  }

  private prepareOpenInvoices(prepared: InvoceInlist[]) {
    prepared.forEach((invoice) => {
      if (!invoice.gastronomyTable) return;
      this.hallList.forEach((hall) => {
        hall.gastronomyTables.forEach((table) => {
          if (table?.uuid === invoice?.gastronomyTable?.uuid) invoice.hallName = hall.name;
        });
      });
    });
  }

  public getDataFromDb2(start: number, end: number) {
    const queryParams = this.buildFilter();
    const options: GetAllDataOptions = {
      join: [
        leftJoin(UPDATES_TYPES.Invoice.type, 'e', {
          'e.uuid': property('i.employee.uuid'),
          'e.ext_type': 'employee',
        }),
        leftJoin(UPDATES_TYPES.Invoice.type, 'c', {
          'c.uuid': property('i.customer.uuid'),
          'c.ext_type': 'customer',
        }),
        leftJoin(UPDATES_TYPES.Invoice.type, 's', {
          's.uuid': property('i.store.uuid'),
          's.ext_type': 'store',
        }),
      ],
      offset: start,
      limit: end,
      order: [by('i.fieldToSort', 'descending')],
    };
    let promise = Promise.resolve();
    if (this.currentFilterSet?.tab === 'open' && start === 0) {
      promise = this.loadGastronomyHallItems();
    }

    return promise
      .then(() => this.DbDaoService.getAllData(from(UPDATES_TYPES.Invoice.type, 'i'), queryParams, options))
      .then((data) => {
        return data.data.map((d) => ({
          ...d.i,
          employee: d.e,
          customer: d.c,
          store: d.s,
        }));
      })
      .then((data) => {
        const prepared = this.InvoiceListService.prepareListForUI(data);
        if (this.currentFilterSet?.tab === 'open' || this.currentFilterSet?.tab === 'paid') {
          this.prepareOpenInvoices(prepared);
        }
        return prepared;
      });
  }

  public getOnlineInvoiceList(start: number, end: number, searchValue: string, subChannel: SalesSubChannelTypesEnum, order: Order = [by('i.fieldToSort', 'descending')]) {
    const { isNotDeleted } = this.queryHelpers('i.');
    const queryParams: Query = query(
      {
        'i.ext_type': isNull(),
        'i.salesChannel': oneOf(SALES_CHANNEL_TYPES.WEBSHOP, SALES_CHANNEL_TYPES.ONLINE),
        'i.subChannel': subChannel,
        'i.invoiceType': oneOf(INVOICE_TYPES.INVOICE, INVOICE_TYPES.CANCELLATION),
      },
      isNotDeleted
    );
    const options: GetAllDataOptions = {
      join: [
        leftJoin(UPDATES_TYPES.Invoice.type, 'c', {
          'c.uuid': property('i.customer.uuid'),
          'c.ext_type': 'customer',
        }),
      ],
      offset: start,
      limit: end,
      order: [...order],
    };

    if (searchValue) {
      queryParams.push(group({ 'i.fieldToSearch': contains(searchValue) }, or({ 'c.fieldToSearch': contains(searchValue) })));
    }

    return this.getWebshopStore()
      .then((storeUuids: string[]) => {
        if (storeUuids) {
          queryParams.push({ 'i.storeUuid': oneOf(...storeUuids) });
          return this.DbDaoService.getAllData(from(UPDATES_TYPES.Invoice.type, 'i'), queryParams, options);
        }
        return { data: [] } as DbResponse;
      })
      .then((data) => {
        return data.data.map((d) => ({
          ...d.i,
          customer: d.c,
        }));
      })
      .then((data) => {
        const prepared = this.InvoiceListService.prepareListForUI(data);
        return prepared;
      });
  }

  public async loadHalls(list: InvoceInlist[]) {
    await this.loadGastronomyHallItems();
    this.prepareOpenInvoices(list);
  }

  public changeTablesInDb(tableUuid, tableName) {
    const queryHelpers = this.queryHelpers();
    const { isDraft, isNotDeleted, storeUuid, isPosSalesChannel } = queryHelpers;
    const queryParams = query({ 'gastronomyTable.uuid': tableUuid }, isDraft, storeUuid, isPosSalesChannel, isNotDeleted);
    this.DbDaoService.getAllData(UPDATES_TYPES.Invoice.type, queryParams)
      .then((data) => {
        if (data && data['data'] && data['data'].length >= 1) {
          data['data'].map((invoice) => {
            if (!tableName) {
              invoice.gastronomyTable = null;
              invoice.gastronomyTableName = null;
            } else {
              invoice.gastronomyTableName = tableName;
            }
            this.InvoicesService.saveInvoice(invoice).catch((err) => this.logger.error(err, 'changeTablesInDb:saveInvoice'));
          });
        }
      })
      .catch((err) => this.logger.error(err, 'changeTablesInDb:getDataByParams'));
  }

  public getNoRoomInvoices() {
    const { isDraft, isNotDeleted, storeUuid, isPosSalesChannel } = this.queryHelpers();

    if (!storeUuid) {
      return;
    }

    const queryParams = query({ gastronomyTable: isNull() }, isDraft, storeUuid, isPosSalesChannel, isNotDeleted);
    return this.invoicesProvider
      .getCountByParams(queryParams)
      .pipe(map((value) => !!value))
      .toPromise();
  }

  public sortInvoicesByDate(list) {
    return list.sort((a, b) =>
      a.date && b.date
        ? new Date(b.date).valueOf() - new Date(a.date).valueOf()
        : new Date(b.modificationDate).valueOf() - new Date(a.modificationDate).valueOf()
    );
  }

  public setTableToHall(tableUuid) {
    let tableName = this.hallList.filter(
      (hall) => hall.gastronomyTables.filter((table) => table.uuid === tableUuid && hall.name)?.length !== 0 && hall.name
    );
    return tableName && tableName?.length !== 0 ? tableName[0]?.name : null;
  }

  public async checkTableExist(tableUuid): Promise<string | null> {
    await this.loadGastronomyHallItems();
    let tableName = this.hallList.filter(
      (hall) => hall.gastronomyTables.filter((table) => table.uuid === tableUuid && !table.deleted)?.length !== 0 && hall.name
    );
    return tableName && tableName?.length !== 0 ? tableName[0]?.name : null;
  }

  public clickInvoiceHandler(invoice, shouldUpdateData: boolean = false) {
    if (!invoice?.isDraft) {
      this.navController
        .navigateForward(ROUTE_URLS.success, {
          state: { invoiceUUID: invoice.uuid, fromState: 'Invoices' },
          animation: mdTransitionAnimation,
        })
        .catch((err) => this.logger.error(err, 'clickInvoiceHandler:navigateForward:success'));
      return;
    }
    this.DbDaoService.getDataByUUID(UPDATES_TYPES.Invoice.type, invoice.uuid)
      .then((data: DbResponse<Invoice>): void => {
        if (data['data']) {
          this.navController
            .navigateRoot(ROUTE_URLS.collection, { state: { fromState: 'Invoices' } })
            .catch((err) => this.logger.error(err, 'clickInvoiceHandler:navigateRoot:collection'));
          this.CartService.setActiveInvoice(new Invoice(data['data']));
        }
      })
      .catch((err) => this.logger.error(err, 'clickInvoiceHandler:getDataByUUID'));
  }

  public newTableInvoice() {
    this.newTableInvoiceEvent.emit(true);
  }

  public newInvoice() {
    this.newInvoiceEvent.emit(true);
  }

  public combineInvoice() {
    return this.navController.navigateForward(ROUTE_URLS.invoicesCombine, { animation: mdTransitionAnimation });
  }

  private queryHelpers(prefix: string = '') {
    const storeUuid: string = this.SecurityService.getActiveStore()?.uuid;
    const isSelfOrderingEnabled: boolean = this.SecurityService.getLoggedCompanyData()?.['isSelfOrderingEnabled'];
    const isPosSalesChannel = isSelfOrderingEnabled
        ? group({ [prefix + 'salesChannel']: SALES_CHANNEL_TYPES.POS }, or ({ [prefix + 'subChannel']: SalesSubChannelTypesEnum.SELFORDER  }))
        : { [prefix + 'salesChannel']: SALES_CHANNEL_TYPES.POS };

      return {
        isDraft: { [prefix + 'isDraft']: true },
        isNotDeleted: group({ [prefix + 'deleted']: isNull() }, or({ [prefix + 'deleted']: false })),
        storeUuid: !!storeUuid ? { [prefix + 'storeUuid']: storeUuid } : null,
        isPosSalesChannel,
      };
  }

  private getWebshopStore() {
    const { isNotDeleted } = this.queryHelpers();
    const queryParams: QueryArray = query({ isWebshopStore: true }, isNotDeleted);
    return this.storesProvider
      .getListByParams(queryParams, { select: ['uuid'] })
      .pipe(map((stores: Store[]): string[] => this.mappingUuidFromStoreArray(stores)))
      .toPromise();
  }

  private mappingUuidFromStoreArray(stores: Store[] = []): string[] {
    const activeStore: Store = this.SecurityService.getActiveStore();
    const webShopInvoices: string[] = stores
      .map((store: Store) => store.uuid)
      .filter((uuid: string) => !!uuid);

    return activeStore.uuid ? [ ...webShopInvoices, activeStore.uuid] : !!webShopInvoices.length ? [ ...webShopInvoices] : null;
  }
}
